/**
 * El set de iconos de la aplicación. Un concepto, un dibujo.
 *
 * Todo lo que hay acá está sobre el grid 24 y saca su color de `currentColor` — las dos reglas
 * que hacen que el set sea un set y no una colección. La geometría y el porqué están en `icon.tsx`;
 * las reglas de construcción, en `.governance/anatomy.md § icon conventions`.
 *
 * Antes de agregar uno: buscalo en `/iconos-catalogo` por nombre o alias. Si ya está, importalo.
 * Si no está, se agrega acá **y** en `catalog.ts` — `npm run audit:icons` verifica que ninguno de
 * los dos lados quede sin el otro, y que no entre un dibujo que ya existe con otro nombre.
 *
 * Reglas que salieron del diagnóstico visual del run (ver `icon-diagnosis.md`):
 *
 * - **Sin `opacity`.** La jerarquía se hace con forma, no con tono: sobre fondos de color el tono
 *   compuesto es impredecible y en un botón deshabilitado las capas tenues desaparecen.
 * - **Luz mínima 2.5 unidades** entre trazos paralelos, y no más de ~3 trazos paralelos. A 14px eso
 *   es 1.46px, que es lo que sobrevive al antialiasing; por debajo el icono se cierra en negro.
 * - **`fill` dentro de `Icon` solo para puntos** (r ≤ 1.8). Una forma con dirección rellena adentro
 *   de un icono de trazo es un híbrido frágil.
 * - **Cabeza de flecha ~4.5–5 de media ala** en todo lo que tenga asta. El único que ocupa más es
 *   `IconChevron`, que es cabeza sola.
 * - **Radios:** 2 en la forma exterior, 1 en detalles interiores.
 * - **Margen ≥2.5** respecto del borde del viewBox. Nada toca el canto.
 */
import { Icon, IconSolid, Rotate, type Direction, type IconProps } from './icon'

export type { IconProps, Direction }

/**
 * La prop se llama `facing` y no `direction` a propósito: `direction` ya es un atributo de
 * presentación de SVG, así que el nombre obvio colisiona con el tipo heredado de
 * `ComponentProps<'svg'>` y además terminaría emitido en el DOM.
 */
interface DirectionalProps extends IconProps {
  facing?: Direction
}

/** En `IconArrow` la dirección es obligatoria — ver el comentario del componente. */
interface RequiredDirectionalProps extends IconProps {
  facing: Direction
}

/* ── navegación ──────────────────────────────────────────────────── */

/** Base apuntando hacia abajo; las otras tres direcciones son la misma forma rotada. */
export function IconChevron({ facing = 'down', ...props }: DirectionalProps) {
  return (
    <Icon {...props}>
      <Rotate facing={facing}>
        <path d="M6 9l6 6 6-6" />
      </Rotate>
    </Icon>
  )
}

/**
 * `facing` es **requerido**, sin default. Una flecha sin dirección explícita es un bug del call
 * site, y el tipo lo impide gratis. De paso evita que un `<IconArrow />` suelto produzca una flecha
 * hacia abajo que nadie eligió y que se confunde con `IconDownload`.
 */
export function IconArrow({ facing, ...props }: RequiredDirectionalProps) {
  return (
    <Icon {...props}>
      <Rotate facing={facing}>
        <path d="M12 4v16M7 15l5 5 5-5" />
      </Rotate>
    </Icon>
  )
}

/** No rota — por eso el nombre dice su dirección en vez de prometer variantes que no existen. */
export function IconArrowUpRight(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 17L17 7" />
      <path d="M10.5 7H17v6.5" />
    </Icon>
  )
}

export function IconMenu(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </Icon>
  )
}

export function IconHome(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 11.5 12 4l8 7.5V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <path d="M9.5 21v-5.5A1.5 1.5 0 0 1 11 14h2a1.5 1.5 0 0 1 1.5 1.5V21" />
    </Icon>
  )
}

export function IconClose(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  )
}

export function IconDotsVertical(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function IconDotsHorizontal(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Seis puntos: la convención de «esto se arrastra para reordenar». */
export function IconDragHandle(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Caja con la diagonal saliendo: la gramática de «esto sale del producto». */
export function IconExternalLink(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14 4h6v6M20 4l-8 8" />
      <path d="M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
    </Icon>
  )
}

export function IconExpand(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 3H3v6M15 3h6v6M3 15v6h6M21 15v6h-6" />
    </Icon>
  )
}

export function IconSearch(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 3.5 3.5" />
    </Icon>
  )
}

/** El «+» va sobredimensionado a propósito: a 14px es lo único que lo separa de `IconSearch`. */
export function IconZoom(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 3.5 3.5" />
      <path d="M11 7.5v7M7.5 11h7" />
    </Icon>
  )
}

/* ── acciones ────────────────────────────────────────────────────── */

export function IconPlus(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  )
}

export function IconMinus(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
    </Icon>
  )
}

export function IconCheck(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12.5l5 5L20 6" />
    </Icon>
  )
}

export function IconPencil(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20h4L20 8l-4-4L4 16v4Z" />
      <path d="M13.5 6.5l4 4" />
    </Icon>
  )
}

export function IconTrash(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M5.5 7l1 12.5a1.5 1.5 0 0 0 1.5 1.4h8a1.5 1.5 0 0 0 1.5-1.4L18.5 7" />
      <path d="M9 7V4.5h6V7" />
    </Icon>
  )
}

export function IconCopy(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h8" />
    </Icon>
  )
}

/** Bandeja + flecha: la gramática de «transferencia». Su par es `IconUpload`. */
export function IconDownload(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />
    </Icon>
  )
}

export function IconUpload(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 15V3M7.5 7.5 12 3l4.5 4.5" />
      <path d="M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />
    </Icon>
  )
}

/**
 * Documento con la flecha cruzando el borde. Reemplaza al viejo `IconExport` —que era el mismo
 * dibujo que `IconExternalLink`— y absorbe a `IconReportOut`: emitir un reporte y sacar datos en
 * otro formato eran dos nombres para el mismo concepto.
 */
export function IconExport(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14.5 3.5H5.5A2 2 0 0 0 3.5 5.5v13a2 2 0 0 0 2 2h9" />
      <path d="M7 8.5h5M7 12h3.5" />
      <path d="M12.5 16h8M18 13.5l2.5 2.5-2.5 2.5" />
    </Icon>
  )
}

export function IconRefresh(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v5h-5" />
    </Icon>
  )
}

export function IconGear(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </Icon>
  )
}

export function IconFilter(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />
    </Icon>
  )
}

/** Dos flechas opuestas: ordenar una columna, no moverse por ella. */
export function IconSort(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 4v16M3.5 16.5 7 20l3.5-3.5" />
      <path d="M17 20V4M13.5 7.5 17 4l3.5 3.5" />
    </Icon>
  )
}

export function IconEye(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  )
}

export function IconEyeOff(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 12s3.6-6.5 10-6.5c1.7 0 3.2.4 4.5 1M22 12s-3.6 6.5-10 6.5c-1.7 0-3.2-.4-4.5-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M3 3l18 18" />
    </Icon>
  )
}

export function IconLock(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </Icon>
  )
}

export function IconPlay(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M6 4l14 8-14 8V4Z" />
    </IconSolid>
  )
}

/**
 * El rayo de «ejecutar». Sólido como `IconPlay` y por el mismo criterio: lo que tiene efecto sobre el
 * sistema pesa más que lo que solo lo muestra.
 *
 * Es un icono distinto de `IconPlay` a propósito, aunque los dos «arranquen» algo: `IconPlay` corre
 * una línea de tiempo, que es reversible y no cambia nada; esto dispara una operación que sí deja
 * consecuencia. El día que las dos cosas convivan en la misma lista, la diferencia tiene que verse.
 */
export function IconExecute(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M13 2L3 14h9l-1 8L21 10h-9l1-8Z" />
    </IconSolid>
  )
}

export function IconPause(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M7 4h3.5v16H7zM13.5 4H17v16h-3.5z" />
    </IconSolid>
  )
}

export function IconSkipBack(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M6 5h2v14H6zM20 6l-10 6 10 6V6Z" />
    </IconSolid>
  )
}

export function IconSkipForward(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M18 5h-2v14h2zM4 6l10 6-10 6V6Z" />
    </IconSolid>
  )
}

/* ── estado ──────────────────────────────────────────────────────── */

export function IconAlert(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function IconInfo(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11.5v5" />
      <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Resultado contenido: el tilde pelado no se lee sobre un chip de color. Su par es `IconCloseCircle`. */
export function IconCheckCircle(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12.5l2.8 2.8L16.5 9" />
    </Icon>
  )
}

export function IconCloseCircle(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </Icon>
  )
}

export function IconClock(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.5l3.5 2.5" />
    </Icon>
  )
}

export function IconFlag(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 3v18" />
      <path d="M5 4h13l-3 4 3 4H5" />
    </Icon>
  )
}

export function IconStar(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M12 2.5l2.95 5.98 6.6.96-4.77 4.65 1.12 6.57L12 17.55l-5.9 3.11 1.12-6.57L2.45 9.44l6.6-.96L12 2.5Z" />
    </IconSolid>
  )
}

/** El estado apagado del favorito. Mismo dibujo que `IconStar`, en trazo. */
export function IconStarOutline(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5l2.95 5.98 6.6.96-4.77 4.65 1.12 6.57L12 17.55l-5.9 3.11 1.12-6.57L2.45 9.44l6.6-.96L12 2.5Z" />
    </Icon>
  )
}

export function IconBell(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3a7 7 0 0 0-7 7v4l-2 3h18l-2-3v-4a7 7 0 0 0-7-7Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Icon>
  )
}

export function IconWifi(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 8.5a16 16 0 0 1 20 0" />
      <path d="M5.5 12.5a11 11 0 0 1 13 0" />
      <path d="M9 16.5a6 6 0 0 1 6 0" />
      <circle cx="12" cy="20" r="1.4" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Señal celular. Los dataloggers de campo salen por SIM, no por wifi: no son el mismo medio. */
export function IconSignal(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M3 15h3v6H3zM8.5 11.5h3V21h-3zM14 8h3v13h-3zM19.5 4h3v17h-3z" />
    </IconSolid>
  )
}

/** El par de `IconWifi`: los mismos arcos, tachados. Un par de estado comparte la base. */
export function IconDisconnect(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 8.5a16 16 0 0 1 20 0" />
      <path d="M5.5 12.5a11 11 0 0 1 13 0" />
      <path d="M9 16.5a6 6 0 0 1 6 0" />
      <circle cx="12" cy="20" r="1.4" fill="currentColor" stroke="none" />
      <path d="M3 3l18 18" />
    </Icon>
  )
}

/* ── dominio ─────────────────────────────────────────────────────── */

export function IconDrop(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3s-7 7.6-7 11.5a7 7 0 0 0 14 0C19 10.6 12 3 12 3Z" />
    </Icon>
  )
}

export function IconWaves(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 6c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      <path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      <path d="M2 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
    </Icon>
  )
}

/** Medición instantánea — caudal, presión. `IconTrending` es el histórico; esto es el ahora. */
export function IconGauge(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 18.5a8.5 8.5 0 1 1 17 0" />
      <path d="M12 18.5l5.5-6.5" />
      <circle cx="12" cy="18.5" r="1.6" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function IconRack(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M4 9h16M4 15h16" />
      <circle cx="7.5" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="18" r="1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Caja vertical con antena. La antena es lo que dice «equipo de campo» y lo separa del calendario. */
export function IconDevice(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="6.5" y="7" width="11" height="14" rx="2" />
      <path d="M15.5 7V3.5" />
      <path d="M9.5 11h5" />
      <circle cx="12" cy="16.5" r="1.5" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function IconMonitor(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Icon>
  )
}

export function IconMapPin(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 21.5S5.5 15 5.5 9.5a6.5 6.5 0 0 1 13 0C18.5 15 12 21.5 12 21.5Z" />
      <circle cx="12" cy="9.5" r="2" />
    </Icon>
  )
}

export function IconMap(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 5l6-2 8 3 6-2v16l-6 2-8-3-6 2V5Z" />
      <path d="M8 3v16M16 6v14" />
    </Icon>
  )
}

/**
 * La misma silueta plegada de `IconMap` más un pin: cierra el trío con `IconMapPin`, y un par
 * comparte la base.
 *
 * Dos cosas salieron de la densidad, no del gusto. El pin **desplaza** el doblez derecho en vez de
 * convivir con él: centrado dejaba 1.75 unidades de luz contra el doblez izquierdo y se solapaba con
 * el derecho, así que va sobre la mitad derecha y sobrevive un solo doblez — a 14px cuatro trazos
 * paralelos se cierran en negro. Y **no lleva el punto interior** que sí tiene `IconMapPin`: a esta
 * escala (cabeza r=3.25 contra 6.5) el agujero dejaría 0.85 unidades entre el punto y el trazo de la
 * cabeza, 0.5px a 14px. El hueco de la cabeza hace ese trabajo con 4.5 unidades de luz.
 */
export function IconMapPoints(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 5l6-2 8 3 6-2v16l-6 2-8-3-6 2V5Z" />
      <path d="M13.5 19S9.5 14.5 9.5 12a4 4 0 0 1 8 0c0 2.5-4 7-4 7Z" />
    </Icon>
  )
}

export function IconFactory(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 21V12.5h10.5V21" />
      <path d="M17 21V6.5h3.5V21" />
      <path d="M2.5 21h19" />
    </Icon>
  )
}

export function IconBattery(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="7" width="16" height="10" rx="2.5" />
      <path d="M21.5 10.5v3" />
    </Icon>
  )
}

/** Llave horizontal con dientes. La vertical con mango diagonal se leía lupa. */
export function IconKey(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="7" cy="12" r="3.5" />
      <path d="M10.5 12H21" />
      <path d="M17.5 12v3.5M14 12v2.5" />
    </Icon>
  )
}

export function IconPeople(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20.5a6.5 6.5 0 0 1 13 0" />
      <circle cx="17.5" cy="9.5" r="2.5" />
      <path d="M18 14.5c2 .7 3.5 2.6 3.5 5" />
    </Icon>
  )
}

/** Una persona: mi cuenta, mi perfil. El conjunto del sistema es `IconPeople`. */
export function IconUser(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </Icon>
  )
}

/*
 * Los cinco que siguen son **parámetros medidos**, no medios de medición: nombran la magnitud que un
 * sensor reporta, que es la unidad con la que se configura una alerta. Por eso son de dominio y no de
 * datos —`IconTrending` describe la forma de una serie, éstos describen qué hay en ella— y por eso no
 * colisionan con `IconGauge` (el instrumento, que sirve para presión o caudal indistintamente) ni con
 * `IconDrop`/`IconWaves` (el agua como sujeto, no una magnitud suya).
 */

/** El bulbo va relleno: a 14px un contorno de 3 unidades de diámetro se cierra igual, y relleno lee. */
export function IconThermometer(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0Z" />
      <path d="M12 8.5v6.8" />
    </Icon>
  )
}

/**
 * Nivel: la regla con sus marcas, no una gota. Las tres marcas son desiguales a propósito —larga,
 * corta, larga— porque tres iguales a 14px se leen como un rayado y pierden la idea de escala.
 */
export function IconRuler(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="8" width="18" height="8" rx="1.5" />
      <path d="M7.5 8v3.5M12 8v5M16.5 8v3.5" />
    </Icon>
  )
}

/** Oxígeno disuelto: burbujas de tamaño decreciente subiendo. El escalonado es lo que lo separa de un trío de puntos. */
export function IconBubbles(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="8.5" cy="16" r="3.5" />
      <circle cx="15.5" cy="10" r="2.5" />
      <circle cx="10.5" cy="5.5" r="1.5" />
    </Icon>
  )
}

/** Muestra de laboratorio — el parámetro que se determina analizando, no midiendo en línea. */
export function IconFlask(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 3v6.2L4.6 17.7A2 2 0 0 0 6.4 20.7h11.2a2 2 0 0 0 1.8-3L15 9.2V3" />
      <path d="M7.5 3h9" />
      <path d="M6.6 14.5h10.8" />
    </Icon>
  )
}

/**
 * pH y conductividad: la magnitud electroquímica. El núcleo con dos órbitas cruzadas, que a 14px es
 * la única forma de «químico» que sobrevive — el matraz ya lo ocupa `IconFlask` para la muestra.
 */
export function IconAtom(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="2" />
      <ellipse cx="12" cy="12" rx="9.5" ry="4" transform="rotate(30 12 12)" />
      <ellipse cx="12" cy="12" rx="9.5" ry="4" transform="rotate(-30 12 12)" />
    </Icon>
  )
}

/* ── dominio · sensores de compuerta ─────────────────────────────────
   Los siete glifos del widget `gateSensors` de amaru2
   (`metricsViews/components/widgets/gateSensors/icons.tsx`), portados **verbatim**: los `d` son
   los de producción, carácter por carácter.

   **Y esa es la excepción que llevan, escrita acá para que no se lea como un descuido.** El resto
   del set está dibujado sobre el grid 24 siguiendo las reglas del encabezado —margen ≥2.5, luz
   ≥2.5 entre trazos paralelos, cabeza de flecha de 4.5–5—; estos siete vienen de grids de 40, 43,
   59×48 y 28×27, y entran con un `<g transform>` que los lleva al 24 en vez de redibujarlos. La
   razón es que son el vocabulario de un equipo en terreno: un límite superior, un desacople, una
   parada de emergencia pisada. Redibujarlos «mejor» los aleja de lo que el operador ya reconoce
   en producción, que es lo único que un icono de dominio tiene que hacer. Si alguna vez se
   rediseñan, se rediseñan **en amaru2 primero** y acá se vuelve a copiar.

   El `strokeWidth` de cada uno compensa su escala para que el trazo efectivo sobre el grid 24 sea
   el 2 del set: 2/escala. Sin eso el `LowerLimit`, que se achica a 0,6, se dibujaría con un trazo
   de 1,2 al lado de hermanos de 2.

   **La escala y el translate son parámetros del port, no del dibujo, y se calculan — no se
   heredan del icono de al lado.** `EmergencyStop` y `RemoteOperation` entraron con valores que no
   correspondían a su caja de origen, y el root `<svg>` —que fija el grid 24 en `icon.tsx` y no
   overridea `overflow`— los recortaba: `EmergencyStop` medía 24,78 de ancho —los 56 de su dibujo por
   0,4068, más las 2 unidades del trazo— dentro de una caja de 24, y perdía el extremo de la mano
   sobre el pulsador, que es el único rasgo que lo distingue de `EmergencyStopOff`; `RemoteOperation`
   traía la escala de su par `LocalOperation` (0,5581), que viene de otra caja, y se salía 0,53 por
   la izquierda. Los tres números de cada uno salen de encuadrar el bbox del path en el grid 24 con
   el margen 2,5 del encabezado: `escala = 17 / max(ancho, alto)`, `strokeWidth = 2 / escala`, y el
   translate centra lo que queda. El `d` no se toca — el encuadre no es el dibujo. Ningún gate mira
   esto: `audit:icons` verifica de dónde sale la definición de un icono, no su geometría.

   Lo único que **no** se copió son dos atributos de `EmergencyStop`: su trazo blanco literal —el
   único de los siete que no heredaba el color— y su `vectorEffect` sin escalado. El color sale de
   `currentColor` como en todo el set, y el trazo escala con el icono, que es lo que hace que el
   set se vea parejo a cualquier tamaño. (El eje de literales de `audit:icons` lee el archivo
   entero, comentarios incluidos, así que el atributo viejo se nombra en prosa y no se cita.) */

/** Límite inferior alcanzado: la hoja abajo, contra el lecho. */
export function IconLowerLimit(props: IconProps) {
  return (
    <Icon strokeWidth={3.33} {...props}>
      <g transform="scale(0.6) translate(0 2)">
        <path d="M20 19.8V9M20 19.8L25.4 14.4M20 19.8L14.6 14.4M9.6 27H6M17.4 27H13.8M25.2 27H21.6M34 27H30.4" />
      </g>
    </Icon>
  )
}

/** Límite superior alcanzado: la hoja arriba, contra el tope del marco. */
export function IconUpperLimit(props: IconProps) {
  return (
    <Icon strokeWidth={3.33} {...props}>
      <g transform="scale(0.6) translate(0 2)">
        <path d="M20 16.2L20 27M20 16.2L14.6 21.6M20 16.2L25.4 21.6M30.4 9L34 9M22.6 9L26.2 9M14.8 9L18.4 9M6 9L9.6 9" />
      </g>
    </Icon>
  )
}

/** Desacople: el mecanismo soltó la hoja. Es un eslabón abierto. */
export function IconDisengagement(props: IconProps) {
  return (
    <Icon strokeWidth={3.33} {...props}>
      <g transform="scale(0.6) translate(0 2)">
        <path d="M28.55 18.3125L30.7 16.175H30.675C31.8258 14.9825 32.4563 13.3822 32.4282 11.7252C32.4001 10.0683 31.7156 8.49021 30.525 7.3375C29.3587 6.21274 27.8016 5.58424 26.1812 5.58424C24.5609 5.58424 23.0038 6.21274 21.8375 7.3375L19.6875 9.475M11.4625 17.6875L9.325 19.825C8.17421 21.0175 7.54366 22.6178 7.57178 24.2748C7.5999 25.9317 8.28441 27.5098 9.475 28.6625C10.6413 29.7873 12.1984 30.4158 13.8188 30.4158C15.4391 30.4158 16.9962 29.7873 18.1625 28.6625L20.3 26.525M15 5.5V9.25M7.5 13H11.25M25 26.75V30.5M28.75 23H32.5" />
      </g>
    </Icon>
  )
}

/** Modo de operación **local**: se opera en el tablero, en terreno. El par de `IconRemoteOperation`. */
export function IconLocalOperation(props: IconProps) {
  return (
    <Icon strokeWidth={3.58} {...props}>
      <g transform="scale(0.5581) translate(0 2.5)">
        <path d="M35.0875 27.0949C35.0875 27.3518 35.1895 27.5982 35.3712 27.7798C35.5528 27.9615 35.7992 28.0635 36.0561 28.0635L38.903 28.0635C39.1224 28.0207 39.3498 28.0551 39.5468 28.1608C39.7437 28.2666 39.898 28.4371 39.9835 28.6436C40.069 28.8501 40.0805 29.0799 40.016 29.2939C39.9515 29.5079 39.815 29.693 39.6295 29.8178L33.0087 36.4396C32.9001 36.5482 32.7712 36.6343 32.6294 36.6931C32.4875 36.7519 32.3355 36.7821 32.1819 36.7821C32.0284 36.7821 31.8763 36.7519 31.7345 36.6931C31.5926 36.6343 31.4637 36.5482 31.3552 36.4396L24.7324 29.8168C24.547 29.692 24.4104 29.5069 24.3459 29.2929C24.2814 29.0789 24.2929 28.8492 24.3784 28.6427C24.4639 28.4362 24.6182 28.2656 24.8151 28.1599C25.0121 28.0541 25.2395 28.0198 25.4589 28.0626L28.3068 28.0626C28.5637 28.0626 28.81 27.9605 28.9917 27.7788C29.1734 27.5972 29.2754 27.3508 29.2754 27.0939L29.2754 23.2538C29.2754 22.9969 29.3775 22.7505 29.5591 22.5689C29.7408 22.3872 29.9872 22.2852 30.2441 22.2852L34.1188 22.2852C34.3757 22.2852 34.6221 22.3872 34.8037 22.5689C34.9854 22.7505 35.0875 22.9969 35.0875 23.2538L35.0875 27.0949Z" />
        <path d="M29.3188 10.4442C29.3188 10.1873 29.2167 9.94091 29.0351 9.75925C28.8534 9.57759 28.607 9.47553 28.3501 9.47553L25.5032 9.47553C25.2838 9.51833 25.0564 9.48396 24.8595 9.37823C24.6626 9.2725 24.5083 9.10192 24.4227 8.89542C24.3372 8.68892 24.3257 8.4592 24.3903 8.2452C24.4548 8.0312 24.5913 7.8461 24.7767 7.72127L31.3976 1.09943C31.5061 0.990859 31.635 0.904733 31.7769 0.845972C31.9187 0.787211 32.0708 0.756967 32.2243 0.756967C32.3779 0.756967 32.5299 0.787211 32.6718 0.845972C32.8136 0.904733 32.9425 0.990859 33.0511 1.09943L39.6739 7.72224C39.8593 7.84707 39.9958 8.03217 40.0603 8.24617C40.1248 8.46017 40.1134 8.68989 40.0279 8.89639C39.9423 9.1029 39.788 9.27347 39.5911 9.3792C39.3942 9.48493 39.1668 9.5193 38.9474 9.4765L36.0995 9.4765C35.8426 9.4765 35.5962 9.57856 35.4145 9.76022C35.2329 9.94188 35.1308 10.1883 35.1308 10.4452L35.1308 14.2852C35.1308 14.5421 35.0288 14.7885 34.8471 14.9702C34.6654 15.1518 34.4191 15.2539 34.1622 15.2539L30.2875 15.2539C30.0306 15.2539 29.7842 15.1518 29.6025 14.9702C29.4209 14.7885 29.3188 14.5421 29.3188 14.2852L29.3188 10.4442Z" />
        <path d="M7.50929 8.43476L13.6571 8.43476M10.5832 5.36085L10.5832 11.5087M18.2679 8.43476C18.2679 12.6789 14.8274 16.1195 10.5832 16.1195C6.33902 16.1195 2.89844 12.6789 2.89844 8.43475C2.89844 4.19058 6.33902 0.750001 10.5832 0.750001C14.8274 0.750002 18.2679 4.19058 18.2679 8.43476Z" />
        <path d="M7.50929 29.091L13.6571 29.091M10.5832 26.0171L10.5832 32.1649M18.2679 29.091C18.2679 33.3352 14.8274 36.7758 10.5832 36.7758C6.33902 36.7758 2.89844 33.3352 2.89844 29.091C2.89844 24.8468 6.33902 21.4063 10.5832 21.4063C14.8274 21.4063 18.2679 24.8468 18.2679 29.091Z" />
      </g>
    </Icon>
  )
}

/** Modo de operación **telecomando**: se opera a distancia. El par de `IconLocalOperation`. */
export function IconRemoteOperation(props: IconProps) {
  return (
    <Icon strokeWidth={4.72} {...props}>
      <g transform="scale(0.4233) translate(7.43 9.85)">
        <path d="M31 15.9997H31.01M21 4.81966C23.7501 2.35989 27.3103 1 31 1C34.6897 1 38.2499 2.35989 41 4.81966M24 8.85866C25.8693 7.02639 28.3825 6.00009 31 6.00009C33.6175 6.00009 36.1307 7.02639 38 8.85866M27.5 12.4287C28.4346 11.5125 29.6912 10.9994 31 10.9994C32.3088 10.9994 33.5654 11.5125 34.5 12.4287" />
        <path d="M16.6229 9.20725C14.1941 8.77026 11.6934 9.02735 9.40029 9.9498C7.10715 10.8722 5.11192 12.4237 3.63754 14.4308C2.16316 16.4379 1.26773 18.8216 1.0513 21.3155C0.834877 23.8094 1.30598 26.3153 2.41198 28.553C3.51797 30.7908 5.21528 32.6724 7.31423 33.9875C9.41319 35.3026 11.8311 35.9994 14.2978 36H30.4492C32.717 36 34.892 35.0857 36.4955 33.4581C38.0991 31.8306 39 29.6231 39 27.3214C39 25.0197 38.0991 22.8123 36.4955 21.1848" />
      </g>
    </Icon>
  )
}

/** Parada de emergencia **pisada**: la mano sobre el pulsador. El reposo es `IconEmergencyStopOff`. */
export function IconEmergencyStop(props: IconProps) {
  return (
    <Icon strokeWidth={6.59} {...props}>
      <g transform="scale(0.3036) translate(10.22 15.54)">
        <path d="M7.97221 15.4286L17.9722 15.4286C20.1823 15.4286 22.302 16.3316 23.8648 17.9391C25.4276 19.5465 26.3055 21.7267 26.3055 24C26.3055 26.2733 25.4276 28.4535 23.8648 30.0609C22.302 31.6684 20.1823 32.5714 17.9722 32.5714L7.97221 32.5714M7.97221 15.4286L7.97221 32.5714M7.97221 15.4286C7.97221 14.5193 7.62102 13.6472 6.9959 13.0042C6.37078 12.3612 5.52293 12 4.63888 12L2.97221 12C2.53018 12 2.10626 12.1806 1.7937 12.5021C1.48113 12.8236 1.30554 13.2596 1.30554 13.7143L1.30554 34.2857C1.30554 34.7404 1.48113 35.1764 1.79369 35.4979C2.10626 35.8194 2.53018 36 2.97221 36L4.63887 36C5.52293 36 6.37078 35.6388 6.9959 34.9958C7.62102 34.3528 7.97221 33.4807 7.97221 32.5714" />
        <path d="M42.2105 26.3278L42.2105 21.734C42.2105 20.5156 41.7561 19.3472 40.9473 18.4857C40.1384 17.6242 39.0414 17.1402 37.8976 17.1402C36.7537 17.1402 35.6567 17.6242 34.8479 18.4857C34.0391 19.3472 33.5847 20.5156 33.5847 21.734L33.5847 28.6247C33.5847 30.0029 34.016 31.1513 34.8786 31.8404L46.5233 44.7031M55.1491 35.5155L52.1301 31.8404C51.2675 31.1513 50.8362 30.0029 50.8362 28.6247L50.8362 19.4371C50.8362 16.9105 49.9736 14.6136 48.2485 13.0057L38.7602 2.43996C37.9738 1.55361 36.889 1.0363 35.7445 1.00184C34.6001 0.967382 33.4896 1.41859 32.6574 2.25621C31.8253 3.09383 31.3396 4.24925 31.3073 5.46828C31.2749 6.68732 31.6985 7.87011 32.4849 8.75646L40.895 18.4035M44.3669 47L57.3055 33.2185" />
      </g>
    </Icon>
  )
}

/** Parada de emergencia en reposo: el pulsador solo, sin la mano. El par de `IconEmergencyStop`. */
export function IconEmergencyStopOff(props: IconProps) {
  return (
    <Icon strokeWidth={2.33} {...props}>
      <g transform="scale(0.8571) translate(0 0.5)">
        <path d="M7.97135 4.73326L17.9714 4.73326C20.1815 4.73326 22.3011 5.63632 23.8639 7.24377C25.4267 8.85123 26.3047 11.0314 26.3047 13.3047C26.3047 15.578 25.4267 17.7581 23.8639 19.3656C22.3011 20.9731 20.1815 21.8761 17.9714 21.8761L7.97135 21.8761M7.97135 4.73326L7.97135 21.8761M7.97135 4.73326C7.97135 3.82394 7.62017 2.95187 6.99504 2.30889C6.36992 1.66591 5.52208 1.30469 4.63802 1.30469L2.97136 1.30469C2.52933 1.30469 2.1054 1.4853 1.79284 1.80679C1.48028 2.12828 1.30469 2.56432 1.30469 3.01897L1.30469 23.5904C1.30469 24.0451 1.48028 24.4811 1.79284 24.8026C2.1054 25.1241 2.52933 25.3047 2.97135 25.3047L4.63802 25.3047C5.52207 25.3047 6.36992 24.9435 6.99504 24.3005C7.62016 23.6575 7.97135 22.7854 7.97135 21.8761" />
      </g>
    </Icon>
  )
}

/* ── datos ───────────────────────────────────────────────────────── */

/**
 * Porcentaje: la magnitud que ya viene normalizada a 0-100 (humedad, saturación, apertura de una
 * compuerta). Es de datos y no de dominio porque no nombra qué se mide, sino en qué unidad.
 */
export function IconPercentCircle(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 15.5 15.5 8.5" />
      <circle cx="9.25" cy="9.25" r="1.25" />
      <circle cx="14.75" cy="14.75" r="1.25" />
    </Icon>
  )
}


export function IconTrending(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 17l5.5-6 3.5 3 5.5-7.5 3.5-2.5" />
    </Icon>
  )
}

export function IconChartBars(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M3 12h4v9H3zM10 7h4v14h-4zM17 3h4v18h-4z" />
    </IconSolid>
  )
}

/** Layout asimétrico: es lo que lo separa de `IconViewGrid`, que es una grilla pareja. */
export function IconDashboard(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="3.5" width="7" height="10" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="6" rx="1.5" />
      <rect x="13.5" y="12.5" width="7" height="8" rx="1.5" />
      <rect x="3.5" y="16.5" width="7" height="4" rx="1.5" />
    </Icon>
  )
}

export function IconViewGrid(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M3 3h7.5v7.5H3zM13.5 3H21v7.5h-7.5zM3 13.5h7.5V21H3zM13.5 13.5H21V21h-7.5z" />
    </IconSolid>
  )
}

export function IconViewList(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M2.5 4.5h19v3.5h-19zM2.5 10.25h19v3.5h-19zM2.5 16h19v3.5h-19z" />
    </IconSolid>
  )
}

export function IconListItems(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="5" cy="7" r="1.5" fill="currentColor" stroke="none" />
      <path d="M9.5 7h8" />
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <path d="M9.5 12h8" />
      <circle cx="5" cy="17" r="1.5" fill="currentColor" stroke="none" />
      <path d="M9.5 17h8" />
    </Icon>
  )
}

/** Dos bloques parejos, no un gráfico: sin eje ni alturas distintas, es lo que lo separa de
 * `IconChartBars`. */
export function IconPanels(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="3.5" width="6" height="17" rx="2" />
      <rect x="13.5" y="3.5" width="6" height="17" rx="2" />
    </Icon>
  )
}

export function IconTable(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9.5h18M9 9.5V20" />
    </Icon>
  )
}

export function IconDocument(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 8h8M8 12h6M8 16h4" />
    </Icon>
  )
}

/** Notches cortados hacia adentro en los bordes izquierdo y derecho — la seña que distingue un
 * ticket de un documento cualquiera. La línea punteada es la perforación. */
export function IconTicket(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 6h15A2 2 0 0 1 21.5 8v2.2a1.8 1.8 0 0 0 0 3.6V16a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-2.2a1.8 1.8 0 0 0 0-3.6V8a2 2 0 0 1 2-2Z" />
      <path d="M13 7v10" strokeDasharray="2 2" />
    </Icon>
  )
}

/** El rango de fechas es la interacción central de un producto de series temporales. */
export function IconCalendar(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
      <circle cx="8.5" cy="14.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="14.5" r="1.2" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/* ── comunicación ────────────────────────────────────────────────── */

export function IconMail(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
      <path d="m3 7 9 6.5L21 7" />
    </Icon>
  )
}

/**
 * El logotipo de WhatsApp, adaptado del set de marcas de Font Awesome Free (CC BY 4.0 — la
 * atribución está en `NOTICE.md`).
 *
 * Es el único icono del set que no se dibujó acá, y es deliberado: un brand mark tiene que
 * parecerse a sí mismo antes que a nuestra familia, y a 14px el auricular de Font Awesome conserva
 * la curva reconocible que un redibujo propio perdía.
 *
 * Está ajustado al trazo del set: el glifo va escalado a margen 2.5 y el contorno interior de la
 * burbuja encogido hasta que el anillo mide exactamente 2. Con el anillo original de Font Awesome
 * —1.44 llevado al grid 24— se ve casi un tercio más liviano al lado de `IconMail` o `IconBell`, y
 * engrosarlo hacia adentro en vez de escalar dejaría al auricular tocando el anillo.
 *
 * **Excepción documentada:** la luz entre el auricular y el anillo queda en 1.6 unidades, por
 * debajo del mínimo de 2.5 del set. Es la proporción del logo (Font Awesome la lleva en 1.75) y
 * apretarla más lo distorsionaría. Un brand mark no se rediseña para que cumpla la grilla.
 */
export function IconWhatsapp(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path fillRule="evenodd" clipRule="evenodd" d="M18.65 5.26C16.88 3.48 14.51 2.5 12 2.5C6.8 2.5 2.58 6.72 2.58 11.92C2.58 13.57 3.01 15.19 3.84 16.62L2.5 21.5L7.49 20.19C8.87 20.94 10.41 21.33 11.99 21.33L12 21.33C17.18 21.33 21.5 17.11 21.5 11.92C21.5 9.4 20.43 7.04 18.65 5.26zM12 19.34C10.66 19.34 9.36 18.98 8.22 18.3L7.95 18.14L5.15 18.88L5.89 16.14L5.72 15.86C4.97 14.68 4.58 13.32 4.58 11.92C4.58 7.83 7.91 4.5 12 4.5C13.98 4.5 15.84 5.27 17.24 6.68C18.64 8.08 19.5 9.94 19.5 11.92C19.5 16.01 16.08 19.34 12 19.34zM16.29 13.89C16.05 13.77 14.9 13.2 14.68 13.12C14.46 13.04 14.31 13.01 14.15 13.24C13.99 13.48 13.54 14.01 13.4 14.17C13.27 14.32 13.13 14.35 12.89 14.23C11.51 13.54 10.6 12.99 9.69 11.43C9.45 11.01 9.93 11.04 10.38 10.14C10.46 9.99 10.42 9.85 10.36 9.73C10.3 9.61 9.83 8.45 9.64 7.98C9.45 7.53 9.25 7.59 9.11 7.58C8.97 7.57 8.81 7.57 8.66 7.57C8.5 7.57 8.25 7.63 8.03 7.86C7.81 8.1 7.21 8.67 7.21 9.83C7.21 10.99 8.05 12.11 8.17 12.26C8.28 12.42 9.82 14.79 12.19 15.82C13.68 16.46 14.26 16.52 15.01 16.41C15.46 16.34 16.4 15.84 16.6 15.29C16.79 14.74 16.79 14.26 16.73 14.17C16.68 14.06 16.52 14 16.29 13.89z" />
    </IconSolid>
  )
}

/* ── anotación ───────────────────────────────────────────────────── */

/**
 * La barra de dibujo sobre un gráfico. Todo este grupo se rehízo en el run: venía de otro set y
 * no compartía ni grid ni peso con el resto — el diagnóstico visual lo marcó como «de otra
 * familia». Ahora son trazo 2 sobre grid 24 como todo lo demás, salvo el cursor y la T, que como
 * contorno no se leen.
 */
export function IconCursor(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M6 3l12.5 10-5.75 1.05 3.25 6.6-2.85 1.35-3.25-6.7L6 19.5Z" />
    </IconSolid>
  )
}

/** Cuatro flechas desde el centro: mover el lienzo. */
export function IconPan(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3.5v17M3.5 12h17" />
      <path d="M9.5 6 12 3.5 14.5 6M9.5 18 12 20.5 14.5 18M6 9.5 3.5 12 6 14.5M18 9.5 20.5 12 18 14.5" />
    </Icon>
  )
}

export function IconRect(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1" />
    </Icon>
  )
}

export function IconEllipse(props: IconProps) {
  return (
    <Icon {...props}>
      <ellipse cx="12" cy="12" rx="8.5" ry="6" />
    </Icon>
  )
}

export function IconLine(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20L20 4" />
    </Icon>
  )
}

/** Una sola T. La «Tt» de dos cuerpos se fundía en un bloque a 14px. */
export function IconText(props: IconProps) {
  return (
    <IconSolid {...props}>
      <path d="M5 4h14v3.5h-5.5V20h-3V7.5H5Z" />
    </IconSolid>
  )
}

/** Banderín colgando de una vertical: marca un instante. */
export function IconVMarker(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7.5 3h9L12 9.5Z" fill="currentColor" stroke="none" />
      <path d="M12 9.5V21" />
    </Icon>
  )
}

export function IconUndo(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8.5 13.5 4 9l4.5-4.5" />
      <path d="M4 9h10.25a5.75 5.75 0 0 1 0 11.5H10" />
    </Icon>
  )
}

export function IconRedo(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15.5 13.5 20 9l-4.5-4.5" />
      <path d="M20 9H9.75a5.75 5.75 0 0 0 0 11.5H14" />
    </Icon>
  )
}

/* ── marca ───────────────────────────────────────────────────────── */

/** Isotipo, no icono funcional: no se usa como acción ni como estado, y nunca por debajo de 20px. */
export function IconAmaru(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 20 12 4l9 16H3Z" />
      <path d="M8.5 20 12 13.5 15.5 20" />
    </Icon>
  )
}
